<template>
  <v-dialog :value="visible" width="600px" scrollable>
    <v-card :loading="loading">
      <v-card-title> Lägg till ny bild eller video </v-card-title>
      <v-card-text>
        <div class="search-row">
          <v-text-field
            hide-details
            outlined
            :placeholder="allowPasteUrl ? 'Sök eller skriv in url, https://...' : 'Sökord'"
            v-model="search"
            @keyup.enter="fetch"
          ></v-text-field>
          <v-btn @click="fetch" color="primary" large> Hämta </v-btn>
        </div>
        <div class="images py-3">
          <template v-if="userHasPastedURL && !contentTypeVideo">
            <v-img :src="images[0]" :lazy-src="images[0]" />
          </template>
          <template v-if="userHasPastedURL && contentTypeVideo === 'youtube'">
            <iframe
              class="video-iframe"
              allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
              :src="youTubeEmbedUrl"
            ></iframe>
          </template>
          <template v-if="userHasPastedURL && contentTypeVideo === 'vimeo'">
            <iframe
              class="video-iframe"
              allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
              :src="vimeoEmbedUrl"
            ></iframe>
          </template>
          <template v-else>
            <Item
              class="mb-2"
              v-for="image in images"
              :key="image.id"
              v-bind="image"
              @select="save"
            />
          </template>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="close"> Stäng </v-btn>
        <v-btn color="primary" v-if="userHasPastedURL" text @click="save">Lägg till</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import Item from './ImagePickItem.vue'
const STATUS_ONLINE = 'online'

export default {
  name: 'ImagePick',
  props: {
    visible: {
      type: Boolean,
    },
    allowPasteUrl: { type: Boolean, default: false },
  },
  components: {
    Item,
  },
  data() {
    return {
      search: '',
      loading: false,
    }
  },
  watch: {
    visible(val) {
      if (!val) {
        this.clearAll()
      }
    },
  },
  computed: {
    ...mapGetters('imageSearch', { imagesFromBackend: 'getArray' }),
    images() {
      if (this.userHasPastedURL) {
        return [this.search]
      }
      return this.imagesFromBackend
    },
    userHasPastedURL() {
      return this.search.startsWith('http')
    },

    youTubeEmbedUrl() {
      return this.search.replace('watch?v=', 'embed/')
    },

    vimeoEmbedUrl() {
      const id = this.search.split('/').at(-1)
      return `https://player.vimeo.com/video/${id}`
    },

    contentTypeVideo() {
      if (!this.userHasPastedURL) return null
      const vimeoPattern = /vimeo.com\/(\d+)/
      const youtubePattern = /youtube.com\/watch\?v=(\w+)/
      const youtubeEmbedPattern = /youtube.com\/embed\/(\w+)/
      const youtubeShortPattern = /youtu.be\/(\w+)/
      const videoPattern = /mp4$|ogg$|avi$|mpg$|mpeg$/

      if (vimeoPattern.test(this.search)) {
        return 'vimeo'
      }

      if (
        youtubePattern.test(this.search) ||
        youtubeShortPattern.test(this.search) ||
        youtubeEmbedPattern.test(this.search)
      ) {
        return 'youtube'
      }

      if (videoPattern.test(this.search)) {
        return 'video'
      }

      return null
    },
  },
  methods: {
    ...mapMutations('imageSearch', {
      addFilter: 'ADD_FILTER',
      setSortOrder: 'SET_SORT',
      clearStore: 'CLEAR',
    }),
    ...mapActions('imageSearch', { getImages: 'readCollection' }),
    async fetch() {
      if (this.userHasPastedURL) {
        return
      }
      this.loading = true
      this.addFilter({ key: 'search', value: this.search })
      await this.getImages()
      this.loading = false
    },
    clearAll() {
      this.clearStore()
      this.search = ''
      this.loading = false
    },
    close() {
      this.clearAll()
      this.$emit('close')
    },
    save(payload) {
      if (this.userHasPastedURL) {
        if (this.contentTypeVideo === 'youtube') {
          this.$emit('change', { url: this.youTubeEmbedUrl, type: 'video' })
          this.clearAll()
          return
        }
        if (this.contentTypeVideo === 'vimeo') {
          this.$emit('change', { url: this.vimeoEmbedUrl, type: 'video' })
          this.clearAll()
          return
        }
        this.$emit('change', { url: this.search, type: this.contentTypeVideo ? 'video' : 'image' })
        this.clearAll()
        return
      }
      this.clearAll()
      this.$emit('change', payload)
    },
  },
  mounted() {
    this.setSortOrder({ key: 'updated_at', value: 'DESC' })
    this.addFilter({ key: 'pageSize', value: 50 })
    this.addFilter({ key: 'image_publish', value: STATUS_ONLINE })
  },

  beforeDestroy() {
    this.clearAll()
  },
}
</script>

<style lang="less" scoped>
.search-row {
  display: flex;
  gap: 10px;
  width: 100%;
}
.images {
}
video {
  border: 0;
  max-width: 100%;
  min-width: 100%;
  aspect-ratio: 16/9;
}
iframe {
  border: 0;
  max-width: 100%;
  min-width: 100%;
  aspect-ratio: 16/9;
}
</style>
